import { Link } from "react-router-dom";
import { ArrowRight, Clock, Feather, Headphones } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import useSeo from "@/hooks/useSeo";

const RESOURCES = [
  {
    id: "quiet-the-noise",
    eyebrow: "Free Workbook + Guided Meditation",
    title: "Quiet the Noise Starter Kit",
    description:
      "A short PDF workbook and 10-minute guided meditation to soften the inner critic, recognise your noise, get curious about it, and step away. Your first real moment of peace — free.",
    meta: "PDF + 10-minute audio",
    ctaLabel: "Get the Quiet the Noise Starter Kit",
    href: "/starter-kit",
    accent: "sage",
    testId: "free-resources-starter-kit",
    icon: Headphones,
  },
  {
    id: "midlife-clarity-guide",
    eyebrow: "Free 15-Minute Reflection Guide",
    title: "Something Has to Change: A Midlife Clarity Guide",
    description:
      "A short reflective guide for anyone who can sense that something about the way they are living is no longer working — even when nothing is obviously wrong. Pause, notice what is happening beneath the surface, and begin naming what may need to change, before rushing to fix anything.",
    meta: "PDF · approx. 15 minutes",
    ctaLabel: "Get the Free Clarity Guide",
    href: "/midlife-clarity-guide",
    accent: "gold",
    testId: "free-resources-clarity-guide",
    icon: Feather,
  },
];

export default function FreeResources() {
  useSeo({
    title: "Free Resources · Pip Bennett",
    description:
      "Two free resources for clearer thinking and meaningful change — the Quiet the Noise Starter Kit and Something Has to Change: A Midlife Clarity Guide. Choose the one that matches what you need.",
    canonical: "https://pipbennett.com/free-resources",
  });

  return (
    <div data-testid="page-free-resources">
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 md:pt-20 pb-14 md:pb-16">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 100%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-sage">Free Resources</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-ink leading-[1.05]">
              Free resources for clearer thinking and <em className="italic text-sage">meaningful change</em>.
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-ink-soft leading-relaxed">
              Whether you are feeling overwhelmed by the noise in your head or beginning to question whether
              the life you are living still fits, these free resources offer a practical place to begin.
            </p>
            <p className="mt-4 text-sm text-ink-soft/80 italic max-w-xl mx-auto">
              Choose the one that best matches what you need right now — they are complementary, not
              alternatives.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* THE TWO RESOURCE CARDS */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {RESOURCES.map((r, i) => {
              const Icon = r.icon;
              const isSage = r.accent === "sage";
              return (
                <SectionReveal key={r.id} delay={i * 100}>
                  <article
                    className="h-full flex flex-col bg-white border border-hairline rounded-2xl p-8 md:p-10 hover:shadow-lg transition-shadow"
                    data-testid={r.testId}
                  >
                    <div
                      className={`w-12 h-12 rounded-full inline-flex items-center justify-center mb-6 ${
                        isSage ? "bg-sage/10 text-sage" : "bg-gold/15 text-gold-dark"
                      }`}
                    >
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div className={`eyebrow mb-3 ${isSage ? "text-sage" : "text-gold-dark"}`}>
                      {r.eyebrow}
                    </div>
                    <h2 className="font-serif-display text-2xl md:text-[1.75rem] text-ink leading-tight mb-4">
                      {r.title}
                    </h2>
                    <p className="text-ink-soft leading-relaxed mb-6 flex-1">
                      {r.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ink-soft/70 mb-6">
                      <Clock size={12} strokeWidth={1.5} />
                      {r.meta}
                    </div>
                    <Link
                      to={r.href}
                      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-medium tracking-wide transition-all ${
                        isSage
                          ? "bg-sage hover:bg-sage-dark text-cream"
                          : "bg-ink hover:bg-ink/90 text-cream"
                      }`}
                      data-testid={`${r.testId}-cta`}
                    >
                      {r.ctaLabel}
                      <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                  </article>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING NOTE */}
      <section className="py-20 md:py-24 bg-cream-soft border-t border-hairline">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <p className="font-serif-display text-2xl md:text-3xl text-ink italic leading-relaxed">
              You don&rsquo;t need a big, loud moment to take your own life seriously.
            </p>
            <p className="mt-6 text-ink-soft leading-relaxed">
              Both resources are yours &mdash; free, no strings. You&rsquo;ll also be invited to join{" "}
              <em className="italic">Sundays with Pip</em>, a gentle weekly note (unsubscribe anytime).
            </p>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
